import { useTranslation } from 'react-i18next';
import { Control } from 'react-hook-form';
import { BellRing, Play, Vibrate } from 'lucide-react';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ALARM_SOUNDS, AlarmSoundKey, triggerAlarm } from '@/lib/task-alarm';

interface Props {
  control: Control<any>;
  alarmEnabled: boolean;
  currentSound: AlarmSoundKey;
  vibrate: boolean;
}

export function ShiftAlarmSettings({ control, alarmEnabled, currentSound, vibrate }: Props) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BellRing className="w-4 h-4 text-primary" />
          {t('shiftTemplates.alarmSection')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={control}
          name="alarm_enabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-primary" />
                <div>
                  <FormLabel className="text-base font-medium cursor-pointer">
                    {t('shiftTemplates.alarmEnabled')}
                  </FormLabel>
                  <FormDescription className="text-xs">
                    {t('shiftTemplates.alarmEnabledDesc')}
                  </FormDescription>
                </div>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        {alarmEnabled && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <p className="text-xs text-muted-foreground">
              {t('shiftTemplates.alarmBackgroundNote')}
            </p>

            <FormField
              control={control}
              name="alarm_sound"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shiftTemplates.alarmSound')}</FormLabel>
                  <div className="flex items-center gap-2">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ALARM_SOUNDS.map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            {t(s.i18nKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={t('shiftTemplates.alarmTest')}
                      onClick={() => triggerAlarm((field.value as AlarmSoundKey) || currentSound, vibrate)}
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  </div>
                  <FormDescription className="text-xs">
                    {t('shiftTemplates.alarmTest')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="alarm_vibrate"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Vibrate className="w-5 h-5 text-info" />
                    <div>
                      <FormLabel className="text-base font-medium cursor-pointer">
                        {t('shiftTemplates.alarmVibrate')}
                      </FormLabel>
                      <FormDescription className="text-xs">
                        {t('shiftTemplates.alarmVibrateDesc')}
                      </FormDescription>
                    </div>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="alarm_repeat_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shiftTemplates.alarmRepeatMinutes')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t('shiftTemplates.alarmRepeatMinutesDesc')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="alarm_max_repeats"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shiftTemplates.alarmMaxRepeats')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={20}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t('shiftTemplates.alarmMaxRepeatsDesc')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
